import { type FC } from "react";

import { clsx } from "clsx";

import { type AvatarFragment } from "@app_schema";

import { Initials } from "./initials";
import { Skeleton } from "./skeleton";

const avifSrcSet = (avatar: AvatarFragment) =>
  [`${avatar.url_HD_AVIF} 80w`, `${avatar.url_SD_AVIF} 40w`].join(",");

const webpSrcSet = (avatar: AvatarFragment) =>
  [`${avatar.url_HD_WEBP} 80w`, `${avatar.url_SD_WEBP} 40w`].join(",");

// `raised`: a white tile with a faint ring, to hold on the grey rail.
export const Avatar: FC<{
  user?: {
    name: string;
    avatar?: AvatarFragment | null;
  };
  raised?: boolean;
}> = ({ user, raised }) => {
  if (!user)
    return <Skeleton className={"h-10 w-10 rounded-md bg-slate-200"} />;
  const avatar = user.avatar;

  if (avatar) {
    return (
      <figure>
        <source srcSet={avifSrcSet(avatar)} type="image/avif" />
        <source srcSet={webpSrcSet(avatar)} type="image/webp" />

        <img
          className={clsx(
            "h-10 w-10 rounded-md bg-slate-200",
            raised && "shadow-raised",
          )}
          alt={user.name}
          width={40}
          height={40}
          src={avatar.url}
        />
      </figure>
    );
  } else {
    // One quiet tile for everyone: the initials carry who, not a colour.
    return (
      <div
        className={clsx(
          "flex size-10 items-center justify-center rounded-xl text-[13px] font-semibold text-indigo-700",
          raised ? "shadow-raised bg-white" : "bg-[#eff7f6]",
        )}
      >
        <span>
          <Initials name={user.name} />
        </span>
      </div>
    );
  }
};
